import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, Plus, Search, Store } from "lucide-react";

const CATEGORIES = ["general", "restauracio", "moda", "salut", "cultura", "serveis", "esports"];
const empty = { title: "", description: "", image_url: "", start_date: "", end_date: "", conditions: "", category: "general" };

export default function AssocMarketplace() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [showFavs, setShowFavs] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    const { data } = await api.get("/promotions", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, category]);

  const create = async () => {
    try {
      await api.post("/promotions", form);
      toast.success("Promoció publicada"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggleFav = async (id) => {
    await api.post(`/promotions/${id}/favorite`);
    load();
  };

  const visible = showFavs ? items.filter(p => p.favorited_by?.includes(user?.id)) : items;
  const now = new Date();
  const sorted = visible.sort((a, b) => {
    const aActive = new Date(a.start_date) <= now && new Date(a.end_date) >= now;
    const bActive = new Date(b.start_date) <= now && new Date(b.end_date) >= now;
    return (bActive - aActive) || (new Date(b.start_date) - new Date(a.start_date));
  });

  return (
    <div className="space-y-6" data-testid="assoc-marketplace">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-accent">Marketplace</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Avantatges entre associats</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="promo-new-btn"><Plus className="w-4 h-4 mr-2" /> Nova promoció</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nova promoció</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Títol</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="promo-form-title" /></div>
              <div><Label>Descripció</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Imatge (URL)</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Inici</Label><Input type="date" value={form.start_date?.slice(0, 10)} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>Fi</Label><Input type="date" value={form.end_date?.slice(0, 10)} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <div><Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Condicions</Label><Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} data-testid="promo-form-save">Publicar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca promocions" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-full w-64" data-testid="promo-search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="rounded-full w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Totes</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant={showFavs ? "default" : "outline"} className="rounded-full" onClick={() => setShowFavs(!showFavs)} data-testid="promo-favs-toggle">
          <Heart className={`w-4 h-4 mr-2 ${showFavs ? "fill-current" : ""}`} /> Favorites
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map(p => {
          const fav = p.favorited_by?.includes(user?.id);
          const active = new Date(p.start_date) <= new Date() && new Date(p.end_date) >= new Date();
          return (
            <Card key={p.id} className="rounded-2xl border-2 overflow-hidden" data-testid={`promo-card-${p.id}`}>
              {p.image_url ? (
                <img src={p.image_url} alt="" className="aspect-video w-full object-cover" />
              ) : (
                <div className="aspect-video w-full bg-primary/10 flex items-center justify-center"><Store className="w-10 h-10 text-primary/40" /></div>
              )}
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest ${active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{active ? "Activa" : "Caducada"}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{p.category}</span>
                </div>
                <div className="font-heading font-semibold text-lg leading-tight">{p.title}</div>
                <div className="text-xs text-muted-foreground mt-1">Per {p.associate?.commercial_name || "—"}</div>
                <div className="text-sm text-muted-foreground line-clamp-2 mt-2">{p.description}</div>
                <div className="text-xs text-muted-foreground mt-2">{p.start_date?.slice(0, 10)} → {p.end_date?.slice(0, 10)}</div>
                <div className="mt-3 flex items-center justify-between">
                  <Button size="sm" variant="ghost" onClick={() => toggleFav(p.id)} data-testid={`promo-fav-${p.id}`}>
                    <Heart className={`w-4 h-4 mr-1 ${fav ? "fill-red-500 text-red-500" : ""}`} /> {p.favorited_by?.length || 0}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {sorted.length === 0 && <div className="col-span-full text-sm text-muted-foreground text-center py-8">Sense promocions.</div>}
      </div>
    </div>
  );
}
